import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

export default function DestructiveActionDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Keep',
  confirmationText = '',
  confirmationLabel = '',
  busy = false,
  onConfirm,
  onClose,
  children,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const [typedValue, setTypedValue] = useState('');
  const initialFocusRef = useRef(null);
  const requiresTyping = Boolean(confirmationText);
  const confirmed = !requiresTyping || typedValue === confirmationText;

  useEffect(() => {
    if (!isOpen) return undefined;
    setTypedValue('');
    window.setTimeout(() => initialFocusRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="destructive-dialog-backdrop" onMouseDown={() => !busy && onClose?.()}>
      <section
        className="destructive-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 id={titleId}>{title}</h2>
        <p id={descriptionId}>{message}</p>
        {children}
        {requiresTyping && (
          <label className="destructive-dialog__confirmation">
            <span>{confirmationLabel || <>Type <strong>{confirmationText}</strong> to confirm</>}</span>
            <input
              ref={initialFocusRef}
              className="input"
              value={typedValue}
              onChange={(event) => setTypedValue(event.target.value)}
              disabled={busy}
              autoComplete="off"
            />
          </label>
        )}
        <div className="destructive-dialog__actions">
          <button
            ref={requiresTyping ? undefined : initialFocusRef}
            type="button"
            className="button button-secondary"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="button button-danger"
            onClick={onConfirm}
            disabled={busy || !confirmed}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

DestructiveActionDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmationText: PropTypes.string,
  confirmationLabel: PropTypes.node,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};
